import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { buscarProductosEnCatalogo, buscarPorSustancia, obtenerSustancias } from '../services/catalogo';
import { buscarMedicamentosPorTexto } from '../services/medicamentos';
import { addDocument, getDocuments } from '../services/database';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Card, CardBody } from '../components/common/Card';
import { Modal } from '../components/common/Modal';
import { Search, Barcode, Building2, Pill, CheckCircle2, Plus, Filter, Sparkles } from 'lucide-react';

const PageContainer = styled.div`
  padding: 24px 20px;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 12px 10px;
  }
`;

const Header = styled.div`
  position: relative;
  padding: 22px 24px;
  margin-bottom: 22px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.06) 0%, rgba(139, 92, 246, 0.09) 100%);
  border: 1px solid rgba(139, 92, 246, 0.2);
  border-radius: 20px;
  overflow: hidden;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.8) inset,
    0 6px 20px -8px rgba(139, 92, 246, 0.18);

  &::before {
    content: '';
    position: absolute;
    top: -40%;
    right: -10%;
    width: 220px;
    height: 220px;
    background: radial-gradient(circle, rgba(139, 92, 246, 0.22) 0%, transparent 65%);
    pointer-events: none;
  }

  h1 {
    position: relative;
    margin: 0;
    font-size: 2rem;
    font-weight: 800;
    letter-spacing: -1px;
    background: linear-gradient(135deg, #4338CA 0%, #8B5CF6 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  p {
    position: relative;
    margin: 4px 0 0;
    font-size: 13.5px;
    color: #64748b;
    font-weight: 500;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 16px 14px;
    margin-bottom: 14px;
    border-radius: 16px;

    h1 {
      font-size: 1.4rem;
      letter-spacing: -0.5px;
    }
  }
`;

const StatsChip = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 6px 12px 6px 8px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.14) 0%, rgba(139, 92, 246, 0.2) 100%);
  border: 1px solid rgba(139, 92, 246, 0.28);
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 600;
  color: #4c1d95;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.5) inset,
    0 2px 8px -2px rgba(139, 92, 246, 0.2);

  .icon {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%);
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 4px rgba(139, 92, 246, 0.4);

    svg {
      width: 12px;
      height: 12px;
      stroke-width: 2.5;
    }
  }

  strong {
    font-weight: 800;
    background: linear-gradient(135deg, #4338CA 0%, #7C3AED 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-variant-numeric: tabular-nums;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 11.5px;
    padding: 5px 10px 5px 6px;
    margin-top: 10px;

    .icon {
      width: 20px;
      height: 20px;

      svg { width: 10px; height: 10px; }
    }
  }
`;

const SearchSection = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  align-items: stretch;
`;

const SearchInputWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 240px;

  svg.search-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    width: 18px;
    height: 18px;
    color: #94a3b8;
    pointer-events: none;
    z-index: 1;
  }

  input {
    padding-left: 42px !important;
  }
`;

const FilterSection = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  align-items: center;
  padding: 14px 16px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(246, 250, 253, 0.92) 100%);
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 16px;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 4px 14px -6px rgba(46, 91, 136, 0.08);
`;

const FilterLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-right: 4px;

  svg {
    width: 14px;
    height: 14px;
  }
`;

const SustanciaTag = styled.button`
  padding: 7px 14px;
  font-size: 12.5px;
  font-weight: 600;
  border-radius: 999px;
  cursor: pointer;
  transition:
    background 180ms ease,
    color 180ms ease,
    border-color 180ms ease,
    transform 140ms cubic-bezier(0.34, 1.56, 0.64, 1),
    box-shadow 180ms ease;

  background: ${props => props.active
    ? 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)'
    : '#ffffff'};
  color: ${props => props.active ? '#ffffff' : '#475569'};
  border: 1px solid ${props => props.active ? 'transparent' : 'rgba(148, 163, 184, 0.3)'};
  box-shadow: ${props => props.active
    ? '0 1px 0 rgba(255, 255, 255, 0.25) inset, 0 4px 12px -3px rgba(139, 92, 246, 0.4)'
    : '0 1px 2px rgba(15, 23, 42, 0.04)'};

  &:hover {
    transform: translateY(-1px);
    ${props => !props.active && `
      background: rgba(139, 92, 246, 0.08);
      color: #6366F1;
      border-color: rgba(139, 92, 246, 0.3);
    `}
  }

  &:active {
    transform: translateY(0) scale(0.96);
  }
`;

const ResultsBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, rgba(79, 179, 191, 0.1) 0%, rgba(46, 91, 136, 0.08) 100%);
  border: 1px solid rgba(79, 179, 191, 0.25);
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 16px;
  font-size: 13.5px;
  color: #2E5B88;

  strong { font-weight: 700; }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
`;

const ProductCard = styled.div`
  position: relative;
  background: linear-gradient(180deg, #ffffff 0%, #fbfdfe 100%);
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 18px;
  padding: 18px;
  cursor: pointer;
  overflow: hidden;
  isolation: isolate;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.95) inset,
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 6px 20px -8px rgba(46, 91, 136, 0.1);
  transition:
    transform 260ms cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 260ms ease,
    border-color 260ms ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #6366F1 0%, #8B5CF6 50%, #6366F1 100%);
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 170px;
    height: 170px;
    background: radial-gradient(circle at top right, rgba(139, 92, 246, 0.1) 0%, transparent 60%);
    pointer-events: none;
    z-index: 0;
  }

  & > * {
    position: relative;
    z-index: 2;
  }

  &:hover:not(.added) {
    transform: translateY(-3px);
    border-color: rgba(139, 92, 246, 0.4);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.95) inset,
      0 1px 3px rgba(15, 23, 42, 0.05),
      0 16px 36px -12px rgba(46, 91, 136, 0.2),
      0 4px 10px -2px rgba(139, 92, 246, 0.15);
  }

  &.added {
    cursor: default;
    border-color: rgba(16, 185, 129, 0.45);
    background: linear-gradient(180deg, #f0fdf4 0%, #ffffff 100%);

    &::before {
      background: linear-gradient(90deg, #10B981 0%, #059669 50%, #10B981 100%);
    }
    &::after {
      background: radial-gradient(circle at top right, rgba(16, 185, 129, 0.12) 0%, transparent 60%);
    }
  }

  h3 {
    margin: 10px 0 0;
    padding: 0 0 14px;
    font-size: 16px;
    font-weight: 700;
    color: #1e293b;
    letter-spacing: -0.3px;
    line-height: 1.3;
    border-bottom: 1px dashed rgba(148, 163, 184, 0.3);
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 12px;
    border-radius: 14px;

    &::before { height: 3px; }

    h3 {
      font-size: 13px;
      margin: 8px 0 0;
      padding: 0 0 10px;
      line-height: 1.25;
    }
  }
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;

  .icon {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.14) 100%);
    color: #6366F1;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg {
      width: 14px;
      height: 14px;
      stroke-width: 2.3;
    }
  }

  .content {
    flex: 1;
    min-width: 0;
  }

  label {
    display: block;
    font-size: 9.5px;
    font-weight: 700;
    color: #94a3b8;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    margin-bottom: 1px;
  }

  p {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1.2;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    gap: 6px;
    padding: 4px 0;

    .icon {
      width: 22px;
      height: 22px;
      border-radius: 6px;

      svg { width: 11px; height: 11px; }
    }

    label { font-size: 8.5px; }
    p { font-size: 11px; }
  }
`;

const PriceBlock = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  margin: 12px 0;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(139, 92, 246, 0.08) 100%);
  border: 1px solid rgba(139, 92, 246, 0.18);
  border-radius: 12px;

  label {
    display: block;
    font-size: 9.5px;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 2px;
  }

  .price {
    font-size: 20px;
    font-weight: 800;
    letter-spacing: -0.5px;
    background: linear-gradient(135deg, #059669 0%, #10B981 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-variant-numeric: tabular-nums;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 8px 10px;
    margin: 10px 0;
    border-radius: 10px;

    .price { font-size: 15px; }
    label { font-size: 8.5px; }
  }
`;

const AddedBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.14) 100%);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 10px;
  color: #047857;
  font-weight: 700;
  font-size: 13px;
  margin-top: 4px;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(246, 250, 253, 0.92) 100%);
  border: 1px dashed rgba(148, 163, 184, 0.35);
  border-radius: 16px;
  color: #94a3b8;
  font-size: 14px;
  font-weight: 500;
`;

export const CatalogSearch = () => {
  const { userId } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [productos, setProductos] = useState([]);
  const [sustancias, setSustancias] = useState([]);
  const [sustanciaSeleccionada, setSustanciaSeleccionada] = useState('');
  const [loading, setLoading] = useState(false);
  const [productosAgregados, setProductosAgregados] = useState(new Set());
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [stockInicial, setStockInicial] = useState('1');
  const [totalResultados, setTotalResultados] = useState(0);

  useEffect(() => {
    cargarSustancias();
    verificarProductosExistentes();
  }, [userId]);

  const cargarSustancias = async () => {
    const listaSustancias = await obtenerSustancias();
    setSustancias(listaSustancias);
  };

  const verificarProductosExistentes = async () => {
    if (!userId) return;
    
    const productosUsuario = await getDocuments(userId, 'products');
    const codigosExistentes = new Set(productosUsuario.map(p => p.barcode));
    setProductosAgregados(codigosExistentes);
  };

  const buscarProductos = async () => {
    setLoading(true);
    try {
      let resultados = [];
      
      if (sustanciaSeleccionada) {
        resultados = await buscarPorSustancia(sustanciaSeleccionada);
      } else if (searchTerm) {
        // Buscar en paralelo en ambas colecciones
        const [catalogoResults, medicamentosResults] = await Promise.all([
          buscarProductosEnCatalogo(searchTerm),
          buscarMedicamentosPorTexto(searchTerm)
        ]);
        
        // Combinar resultados, evitando duplicados por código de barras
        const resultMap = new Map();
        
        // Agregar resultados del catálogo
        catalogoResults.forEach(producto => {
          resultMap.set(producto.codigoBarras, producto);
        });
        
        // Agregar resultados de medicamentos (sobrescribir si ya existe)
        medicamentosResults.forEach(medicamento => {
          resultMap.set(medicamento.codigoBarras, medicamento);
        });
        
        resultados = Array.from(resultMap.values());
        
        // Ordenar por relevancia (los que contienen el término exacto primero)
        if (searchTerm) {
          const termLower = searchTerm.toLowerCase();
          resultados.sort((a, b) => {
            const nombreA = (a.nombre || '').toLowerCase();
            const nombreB = (b.nombre || '').toLowerCase();
            
            // Priorizar coincidencias exactas
            if (nombreA.includes(termLower) && !nombreB.includes(termLower)) return -1;
            if (!nombreA.includes(termLower) && nombreB.includes(termLower)) return 1;
            
            // Luego por coincidencia al inicio
            if (nombreA.startsWith(termLower) && !nombreB.startsWith(termLower)) return -1;
            if (!nombreA.startsWith(termLower) && nombreB.startsWith(termLower)) return 1;
            
            return 0;
          });
        }
      }
      
      // Guardar el total de resultados
      setTotalResultados(resultados.length);
      
      // Limitar a 50 resultados para mejor rendimiento y experiencia
      const resultadosLimitados = resultados.slice(0, 50);
      setProductos(resultadosLimitados);
      
      // Informar al usuario si hay más resultados
      if (resultados.length > 50) {
        console.log(`Mostrando 50 de ${resultados.length} resultados. Use términos más específicos para refinar la búsqueda.`);
      }
    } catch (error) {
      console.error('Error buscando productos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProduct = (producto) => {
    setSelectedProduct(producto);
    setStockInicial('1');
    setShowAddModal(true);
  };

  const agregarAlInventario = async () => {
    if (!selectedProduct) return;
    
    try {
      const nuevoProducto = {
        barcode: selectedProduct.codigoBarras,
        name: selectedProduct.nombre,
        laboratory: selectedProduct.proveedor,
        description: `${selectedProduct.sustancia} - ${selectedProduct.presentacion}`,
        cost: selectedProduct.precios?.real || selectedProduct.precios?.corriente || 0,
        salePrice: selectedProduct.precios?.corriente || 0,
        stock: parseInt(stockInicial),
        invoiceNumber: 'INICIAL',
        entryDate: new Date().toISOString(),
        expirationDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString(), // 1 año por defecto
      };
      
      await addDocument(userId, 'products', nuevoProducto);
      
      setProductosAgregados(prev => new Set([...prev, selectedProduct.codigoBarras]));
      setShowAddModal(false);
      alert(`${selectedProduct.nombre} agregado al inventario con ${stockInicial} unidades`);
    } catch (error) {
      console.error('Error agregando producto:', error);
      alert('Error al agregar el producto');
    }
  };

  return (
    <PageContainer>
      <Header>
        <h1>Buscar en Catálogo</h1>
        <p>Encuentra y agrega productos a tu inventario en segundos</p>
        <StatsChip>
          <div className="icon"><Sparkles /></div>
          <span><strong>+10.000 productos</strong> del mercado colombiano</span>
        </StatsChip>
      </Header>

      <SearchSection>
        <SearchInputWrap>
          <Search className="search-icon" />
          <Input
            type="text"
            placeholder="Buscar por nombre del producto..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && buscarProductos()}
            style={{ marginBottom: 0 }}
          />
        </SearchInputWrap>
        <Button onClick={buscarProductos} disabled={loading}>
          {loading ? 'Buscando...' : 'Buscar'}
        </Button>
      </SearchSection>

      <FilterSection>
        <FilterLabel>
          <Filter />
          Filtrar por tipo
        </FilterLabel>
        <SustanciaTag
          active={sustanciaSeleccionada === ''}
          onClick={() => {
            setSustanciaSeleccionada('');
            setProductos([]);
          }}
        >
          Todos
        </SustanciaTag>
        {sustancias.map(sustancia => (
          <SustanciaTag
            key={sustancia}
            active={sustanciaSeleccionada === sustancia}
            onClick={() => {
              setSustanciaSeleccionada(sustancia);
              setSearchTerm('');
            }}
          >
            {sustancia}
          </SustanciaTag>
        ))}
      </FilterSection>

      {sustanciaSeleccionada && (
        <div style={{ marginBottom: '16px' }}>
          <Button onClick={buscarProductos} disabled={loading}>
            Ver productos de {sustanciaSeleccionada}
          </Button>
        </div>
      )}

      {totalResultados > 50 && (
        <ResultsBanner>
          <Search size={16} />
          <div>
            <strong>Mostrando 50 de {totalResultados} resultados.</strong> Usa términos más específicos para refinar la búsqueda.
          </div>
        </ResultsBanner>
      )}

      <ProductGrid>
        {productos.map(producto => {
          const added = productosAgregados.has(producto.codigoBarras);
          return (
            <ProductCard
              key={producto.codigoBarras}
              onClick={() => !added && handleSelectProduct(producto)}
              className={added ? 'added' : ''}
            >
              <h3>{producto.nombre}</h3>

              <div>
                <InfoRow>
                  <div className="icon"><Barcode /></div>
                  <div className="content">
                    <label>Código</label>
                    <p>{producto.codigoBarras}</p>
                  </div>
                </InfoRow>

                <InfoRow>
                  <div className="icon"><Building2 /></div>
                  <div className="content">
                    <label>Proveedor</label>
                    <p>{producto.proveedor}</p>
                  </div>
                </InfoRow>

                <InfoRow>
                  <div className="icon"><Pill /></div>
                  <div className="content">
                    <label>Tipo</label>
                    <p>{producto.sustancia}{producto.presentacion ? ` · ${producto.presentacion}` : ''}</p>
                  </div>
                </InfoRow>
              </div>

              <PriceBlock>
                <div>
                  <label>Precio sugerido</label>
                  <div className="price">
                    ${producto.precios?.corriente?.toLocaleString() || 'N/A'}
                  </div>
                </div>
              </PriceBlock>

              {added ? (
                <AddedBadge>
                  <CheckCircle2 />
                  Ya en tu inventario
                </AddedBadge>
              ) : (
                <Button
                  size="small"
                  fullWidth
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectProduct(producto);
                  }}
                >
                  <Plus size={14} style={{ marginRight: 4 }} />
                  Agregar al inventario
                </Button>
              )}
            </ProductCard>
          );
        })}
      </ProductGrid>

      {productos.length === 0 && !loading && (searchTerm || sustanciaSeleccionada) && (
        <EmptyState>
          No se encontraron productos. Intenta con otros términos de búsqueda.
        </EmptyState>
      )}

      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Agregar al Inventario"
        size="small"
      >
        {selectedProduct && (
          <div>
            <h4>{selectedProduct.nombre}</h4>
            <p style={{ marginBottom: '16px', color: '#7F8C8D' }}>
              {selectedProduct.proveedor}
            </p>
            
            <Input
              label="Stock inicial"
              type="number"
              value={stockInicial}
              onChange={(e) => setStockInicial(e.target.value)}
              min="1"
            />
            
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <Button variant="outline" onClick={() => setShowAddModal(false)}>
                Cancelar
              </Button>
              <Button onClick={agregarAlInventario}>
                Agregar con {stockInicial} unidades
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </PageContainer>
  );
};